/**
 * Table functionality including sorting, filtering, and search
 */

import { lucideIcon } from '../utils/lucide-utils.js';

export function initializeTableFeatures() {
  // Handle project card clicks
  const projectCards = document.querySelectorAll('.project-card');
  projectCards.forEach((card) => {
    card.addEventListener('click', function () {
      const projectId = this.dataset.projectId;
      window.location.href = `/projects/${projectId}`;
    });
  });

  // Filter tables
  const tableFilters = document.querySelectorAll('.table-filter');
  tableFilters.forEach((filter) => {
    filter.addEventListener('input', function () {
      const searchText = this.value.toLowerCase();
      const tableId = this.dataset.tableTarget;
      const table = document.getElementById(tableId);

      if (!table) return;

      const rows = table.querySelectorAll('tbody tr');

      rows.forEach((row) => {
        let match = false;
        const cells = row.querySelectorAll('td');

        cells.forEach((cell) => {
          if (cell.textContent.toLowerCase().includes(searchText)) {
            match = true;
          }
        });

        if (match) {
          row.style.display = '';
        } else {
          row.style.display = 'none';
        }
      });
    });
  });

  // Handle select all checkboxes
  const selectAllCheckboxes = document.querySelectorAll('.select-all');
  selectAllCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', function () {
      const targetName = this.dataset.target;
      const targetCheckboxes = document.querySelectorAll(`input[name="${targetName}"]`);

      targetCheckboxes.forEach((targetCheckbox) => {
        targetCheckbox.checked = this.checked;
      });
    });
  });

  // Search functionality for tables
  const searchInputs = document.querySelectorAll('[data-table-search]');
  searchInputs.forEach((input) => {
    const targetTable = document.querySelector(input.dataset.tableSearch);
    if (targetTable) {
      input.addEventListener('input', function () {
        const searchTerm = this.value.toLowerCase();
        const rows = targetTable.querySelectorAll('tbody tr');

        rows.forEach((row) => {
          const text = row.textContent.toLowerCase();
          row.style.display = text.includes(searchTerm) ? '' : 'none';
        });
      });
    }
  });
}

// Enhanced table sorting
export function initializeTableSorting() {
  const sortableTables = document.querySelectorAll('table[data-sortable]');
  sortableTables.forEach((table) => {
    const headers = table.querySelectorAll('th[data-sort]');
    headers.forEach((header) => {
      header.style.cursor = 'pointer';
      header.addEventListener('click', function () {
        const sortKey = this.dataset.sort;
        const tbody = table.querySelector('tbody');
        const rows = Array.from(tbody.querySelectorAll('tr'));

        const isAscending = !this.classList.contains('sort-asc');

        // Remove sort classes from all headers
        headers.forEach((h) => h.classList.remove('sort-asc', 'sort-desc'));

        // Add appropriate class for current header
        this.classList.add(isAscending ? 'sort-asc' : 'sort-desc');

        rows.sort((a, b) => {
          const aVal = a.querySelector('[data-sort="' + sortKey + '"]')?.textContent || '';
          const bVal = b.querySelector('[data-sort="' + sortKey + '"]')?.textContent || '';

          return isAscending
            ? aVal.localeCompare(bVal, undefined, { numeric: true })
            : bVal.localeCompare(aVal, undefined, { numeric: true });
        });

        rows.forEach((row) => tbody.appendChild(row));
      });
    });
  });
}

// Handle file sorting functionality
export function initializeFileSorting() {
  const sortOptions = document.querySelectorAll('.sort-file-option');

  // Track current sort state
  const sortState = {
    name: 'asc',
    date: 'asc',
    status: 'asc',
  };

  sortOptions.forEach((option) => {
    option.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      // Get sort parameters from the clicked option
      const sortBy = this.getAttribute('data-sort');

      // Toggle the direction
      if (sortState[sortBy] === 'asc') {
        sortState[sortBy] = 'desc';
      } else {
        sortState[sortBy] = 'asc';
      }

      const direction = sortState[sortBy];

      // Update dropdown button to show current sort
      const buttonSpan = document.querySelector('#sortDropdownButton span');
      // Lucide replaces <i data-lucide> with <svg>, so target svg (or i before init)
      const buttonIcon = document.querySelector('#sortDropdownButton svg, #sortDropdownButton i');

      if (buttonSpan) {
        let sortTypeText = '';
        if (sortBy === 'name') {
          sortTypeText = `File Name (${direction === 'asc' ? 'A-Z' : 'Z-A'})`;
        } else if (sortBy === 'date') {
          sortTypeText = `Date Modified (${direction === 'asc' ? 'Oldest' : 'Newest'})`;
        } else if (sortBy === 'status') {
          sortTypeText = `Status`;
        }

        buttonSpan.textContent = sortTypeText;
      }

      // Swap the chevron icon to reflect current sort direction
      if (buttonIcon) {
        const newIcon = lucideIcon(direction === 'asc' ? 'chevron-up' : 'chevron-down', { size: 14, class: 'me-2' });
        buttonIcon.parentNode.replaceChild(newIcon, buttonIcon);
      }

      // Sort the files table
      const fileTable = document.getElementById('fileTable');
      if (!fileTable) return;

      const tbody = fileTable.querySelector('tbody');
      if (!tbody) return;

      const rows = Array.from(tbody.querySelectorAll('tr'));
      if (rows.length <= 1) return;

      // Sort the rows based on the selected column and direction
      rows.sort((a, b) => {
        let valueA, valueB;

        if (sortBy === 'name') {
          // Sort by file name (first column)
          valueA = a.querySelector('.file-name-cell')?.textContent?.trim() || '';
          valueB = b.querySelector('.file-name-cell')?.textContent?.trim() || '';
        } else if (sortBy === 'date') {
          // Sort by date (fourth column)
          valueA = new Date(a.querySelector('td:nth-child(4)')?.textContent?.trim() || 0);
          valueB = new Date(b.querySelector('td:nth-child(4)')?.textContent?.trim() || 0);
          return direction === 'asc' ? valueA - valueB : valueB - valueA;
        } else if (sortBy === 'status') {
          // Sort by status (third column)
          valueA = a.querySelector('td:nth-child(3) span')?.textContent?.trim() || '';
          valueB = b.querySelector('td:nth-child(3) span')?.textContent?.trim() || '';
        }

        // Compare the values based on direction
        if (direction === 'asc') {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      });

      // Reorder the rows in the table
      rows.forEach((row) => {
        tbody.appendChild(row);
      });
    });
  });
}

// Batch operations for tables
export function initializeBatchOperations() {
  const batchCheckboxes = document.querySelectorAll('input[data-batch]');
  const batchActions = document.querySelector('.batch-actions');

  if (batchCheckboxes.length > 0 && batchActions) {
    const selectAllCheckbox = document.querySelector('#selectAll');

    if (selectAllCheckbox) {
      selectAllCheckbox.addEventListener('change', function () {
        batchCheckboxes.forEach((checkbox) => {
          checkbox.checked = this.checked;
        });
        updateBatchActions();
      });
    }

    batchCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', updateBatchActions);
    });

    function updateBatchActions() {
      const checkedBoxes = document.querySelectorAll('input[data-batch]:checked');
      if (checkedBoxes.length > 0) {
        batchActions.style.display = 'block';
        const countSpan = batchActions.querySelector('.selected-count');
        if (countSpan) {
          countSpan.textContent = checkedBoxes.length;
        }
      } else {
        batchActions.style.display = 'none';
      }
    }
  }
}
